import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { FormEvent, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import FormErrorField from '../../../components/form-error-label';
import Typography from '../../../components/typography';
import { useTranslation } from '../../../hooks/use-translation';
import {
  DocumentToSign,
  DocumentToSignStatus,
  SigningDocumentEnum,
} from '../../../types/document.types';
import { OutletContextProps } from '../application-steps.page';
import ButtonSteps from '../components/buttons-steps';
import SigningDocument from './signing-document';
import { getDocument } from './util';

const COMBINED_FLAG = 'onboarding_combined_docusign_envelope';

const isSigned = (doc: DocumentToSign | undefined): boolean =>
  !!doc?.status &&
  [DocumentToSignStatus.COMPLETED, DocumentToSignStatus.PROCESSING].includes(
    doc.status,
  );

function ReviewAndSignForm(): JSX.Element {
  const { onNextStep, onboardingApplication } =
    useOutletContext<OutletContextProps>();

  const { t } = useTranslation('review-and-sign');
  const [nextClicked, setNextClicked] = useState<boolean>(false);
  // Strict-true check: any other value (undefined, missing flag, provider not
  // ready, resolution error) falls back to the legacy two-envelope flow.
  const combinedMode = useBooleanFlagValue(COMBINED_FLAG, false) === true;

  const contractAgreement = getDocument(
    onboardingApplication,
    SigningDocumentEnum.CONTRACTOR_AGREEMENTS,
  );
  const paic = getDocument(onboardingApplication, SigningDocumentEnum.PAIC);

  const contractAgreementSigned = isSigned(contractAgreement);
  const paicSigned = isSigned(paic);

  const combinedDocument = useMemo<DocumentToSign | undefined>(() => {
    if (!combinedMode) return undefined;
    const base = contractAgreement ?? paic;
    if (!base) return undefined;

    let combinedStatus: DocumentToSignStatus | undefined;
    if (contractAgreementSigned && paicSigned) {
      combinedStatus = DocumentToSignStatus.COMPLETED;
    } else if (
      contractAgreement?.status === DocumentToSignStatus.PROCESSING ||
      paic?.status === DocumentToSignStatus.PROCESSING
    ) {
      combinedStatus = DocumentToSignStatus.PROCESSING;
    }

    return {
      ...base,
      status: combinedStatus,
      docusign_template_id: '',
    };
  }, [
    combinedMode,
    contractAgreement,
    paic,
    contractAgreementSigned,
    paicSigned,
  ]);

  const combinedSigned = isSigned(combinedDocument);

  const allSigned = combinedMode
    ? combinedSigned
    : contractAgreementSigned && paicSigned;

  const onNextStepClicked = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (allSigned) onNextStep();
    setNextClicked(true);
  };

  const combinedRequiredError = nextClicked && !combinedSigned;
  const contractAgreementRequiredError =
    nextClicked && !contractAgreementSigned;
  const paicRequiredError = nextClicked && !paicSigned;

  return (
    <div>
      <form
        className="flex flex-col"
        onSubmit={ev => {
          onNextStepClicked(ev);
        }}
      >
        <Typography size="sm" color="black" className="mb-4">
          {t('PAGE_DESCRIPTION')}
        </Typography>

        {combinedMode && combinedDocument && (
          <div>
            <SigningDocument
              document={combinedDocument}
              documentTitle={t('COMBINED_DOCUMENTS_TITLE')}
              documentDescription={t('COMBINED_DOCUMENTS_DESC')}
              hasErrors={combinedRequiredError}
              combinedMode
            />
            {combinedRequiredError && (
              <FormErrorField>
                {t('REQUIRED_SIGNING', { ns: 'common' })}
              </FormErrorField>
            )}
          </div>
        )}

        {!combinedMode && contractAgreement && (
          <div>
            <SigningDocument
              document={contractAgreement}
              documentTitle={t('CONTRACTORS_AGREEMENTS_PROVIDER_AGREEMENT')}
              documentDescription={t(
                'CONTRACTORS_AGREEMENTS_PROVIDER_AGREEMENT_DESC',
              )}
              hasErrors={contractAgreementRequiredError}
            />
            {contractAgreementRequiredError && (
              <FormErrorField>
                {t('REQUIRED_SIGNING', { ns: 'common' })}
              </FormErrorField>
            )}
          </div>
        )}

        {!combinedMode && paic && (
          <div>
            <SigningDocument
              document={paic}
              documentTitle={t('PAIC_DOCUMENTS')}
              documentDescription={t('PAIC_DOCUMENTS_DESC')}
              hasErrors={paicRequiredError}
            />
            {paicRequiredError && (
              <FormErrorField>
                {t('REQUIRED_SIGNING', { ns: 'common' })}
              </FormErrorField>
            )}
          </div>
        )}

        <ButtonSteps
          hasErrors={
            combinedMode
              ? combinedRequiredError
              : contractAgreementRequiredError || paicRequiredError
          }
        />
      </form>
    </div>
  );
}

export default ReviewAndSignForm;
